import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Navigation } from "@/components/Navigation";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { HeroSection } from "@/components/HeroSection";
import { FeaturedBuild } from "@/components/FeaturedBuild";
import { ServicesSection } from "@/components/ServicesSection";
import { ReferralSection } from "@/components/ReferralSection";
import { BeforeAfterSection } from "@/components/BeforeAfterSection";
import { GallerySection } from "@/components/GallerySection";
import { AboutSection } from "@/components/AboutSection";
import { QuoteForm } from "@/components/QuoteForm";
import { QuoteCTA } from "@/components/QuoteCTA";
import { FooterSection } from "@/components/FooterSection";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { GoogleReviewSection } from "@/components/GoogleReviewSection";
import { TrackJobShowcase } from "@/components/TrackJobShowcase";
import { LoadingScreen } from "@/components/LoadingScreen";
import { Toaster } from "@/components/ui/sonner";
import BookingPage from "@/components/BookingPage";
import DashboardPage from "@/components/DashboardPage";
import TrackJobPage from "@/components/TrackJobPage";
import MobileInspectionPage from "@/components/MobileInspectionPage";
import HeadlightRestorationPage from "@/components/HeadlightRestorationPage";
import BookingSuccessPage from "@/components/BookingSuccessPage";
import QuotePage from "@/components/QuotePage";
import BlogPage from "@/components/BlogPage";
import FAQPage from "@/components/FAQPage";
import AnimatedLandingDemo from "@/pages/AnimatedLandingDemo";
import Logo3DLandingDemo from "@/pages/Logo3DLandingDemo";
import AuthCallback from "@/components/AuthCallback";

const LandingPage = ({ scrollY }) => {
  return (
    <>
      <AnnouncementBanner />
      <Navigation scrollY={scrollY} />
      <HeroSection />
      <FeaturedBuild />
      <div className="section-line" />
      <ServicesSection />
      <div className="section-line" />
      <ReferralSection />
      <div className="section-line" />
      <TrackJobShowcase />
      <div className="section-line" />
      <BeforeAfterSection />
      <div className="section-line" />
      <GallerySection />
      <div className="section-line" />
      <AboutSection />
      <div className="section-line" />
      <GoogleReviewSection />
      <div className="section-line" />
      <QuoteCTA />
      <FooterSection />
    </>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Wrapper to detect OAuth callback (session_id in hash) on /dashboard route
const DashboardWithAuthCheck = () => {
  const location = useLocation();
  
  // Check if this is an OAuth callback (has session_id in hash)
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return <DashboardPage />;
};

function App() {
  const [scrollY, setScrollY] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Show loading screen briefly on initial load
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Remove platform badge
  useEffect(() => {
    const removeBadge = () => {
      const el = document.getElementById('emergent-badge');
      if (el) el.remove();
      document.querySelectorAll('a[href*="emergent-badge"]').forEach(e => e.remove());
    };
    removeBadge();
    const interval = setInterval(removeBadge, 100);
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { clearInterval(interval); observer.disconnect(); };
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white font-body" data-testid="app-container">
      <AnimatePresence>
        {isLoading && <LoadingScreen />}
      </AnimatePresence>
      <div className="noise-overlay" />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<LandingPage scrollY={scrollY} />} />
          <Route path="/book" element={<BookingPage />} />
          <Route path="/quote" element={<QuotePage />} />
          <Route path="/track" element={<TrackJobPage />} />
          <Route path="/dashboard" element={<DashboardWithAuthCheck />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/booking/mobile-inspection" element={<MobileInspectionPage />} />
          <Route path="/booking/headlight" element={<HeadlightRestorationPage />} />
          <Route path="/booking/success" element={<BookingSuccessPage />} />
          <Route path="/portfolio" element={<BlogPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/demo" element={<AnimatedLandingDemo />} />
          <Route path="/demo2" element={<Logo3DLandingDemo />} />
        </Routes>
      </BrowserRouter>
      <WhatsAppButton />
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
// Build timestamp: Tue Mar 10 10:13:42 UTC 2026
